// modules/reporteFacturas/pages/ReporteFacturasPorAnioPage.jsx
// Migracion de index_cinco.php + detalle_servicio.php ("Reporte por Año" +
// "Ver detalle por Servicio") — categorias reales del PHP legado: no son
// servicio.nombre, son combinaciones de cod_servicio+venta (Trunking/
// Trunking Ventas/AVL/Venta AVL/Seguridad/Ventas/Energia/POC) mas
// "Licitación" como catch-all de todo lo que no es tipo='TRS'. Cada
// categoria tiene su columna en Bolivianos y en Dolares, igual que la
// tabla legada.
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box, Button, TextField, MenuItem, Typography, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import CloseIcon from "@mui/icons-material/Close";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { useAuth } from "../../../context/AuthContext";
import { getReportePorAnio, getDetalleMesServicio } from "../services/reporteFacturasService";

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.72rem",
  whiteSpace: "nowrap",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

const SUCURSALES = ["La Paz", "Cochabamba", "Santa Cruz", "Todo"];

const normalizarSucursal = (value) => {
  const s = String(value || "").trim().toLowerCase();
  if (s.includes("santa cruz")) return "Santa Cruz";
  if (s.includes("cochabamba")) return "Cochabamba";
  if (s.includes("la paz") || s.includes("el alto")) return "La Paz";
  return "Todo";
};

const anioActual = new Date().getFullYear();
const ANIOS = Array.from({ length: 12 }, (_, i) => anioActual + 1 - i);

// Coma para miles, punto para decimales — mismo formato que number_format()
// de PHP (usado tal cual en todos los reportes legados que se migraron).
const formatMoneda = (value) =>
  Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// El backend manda "fecha" como string plano "YYYY-MM-DD" (sin hora ni zona
// horaria) — se parsea a mano en vez de "new Date(value)" para no correr el
// dia por el desfase de huso horario (ver misma correccion en
// ReporteFacturasTotalPage / reporteFacturasModel FECHA_REPORTE).
const formatFecha = (value) => {
  const m = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "-";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

// Celda de monto clickeable — abre el detalle de esa categoria+mes. Si no
// hay monto (0), se muestra como texto plano nomas (nada que ver).
// Es un <span> con rol de botón (no un <Button>): el texto de un botón no se puede
// seleccionar, y así al marcar la tabla para pegarla en Excel los montos sí se copian.
// Si hay texto seleccionado, el clic no abre el detalle (era una selección, no un clic).
const CeldaMonto = ({ valor, onClick, bold }) => {
  const abrir = () => {
    if (String(window.getSelection?.() || "").trim()) return;
    onClick();
  };
  return (
    <TableCell align="right">
      {Number(valor) > 0 ? (
        <Box
          component="span" role="button" tabIndex={0} onClick={abrir}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
          sx={{
            cursor: "pointer", color: "primary.main", userSelect: "text", fontWeight: bold ? 700 : 400,
            "&:hover": { textDecoration: "underline" }, "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 1 },
          }}
        >
          {formatMoneda(valor)}
        </Box>
      ) : formatMoneda(valor)}
    </TableCell>
  );
};

const ReporteFacturasPorAnioPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [sucursal, setSucursal] = useState(() => searchParams.get("sucursal") || normalizarSucursal(user?.sucursal));
  const [year, setYear] = useState(() => Number(searchParams.get("year")) || anioActual);
  const [data, setData] = useState(null);
  const [buscado, setBuscado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detalle, setDetalle] = useState(null);
  const [detalleInfo, setDetalleInfo] = useState(null); // { mesNombre, categoriaLabel, categoria } | null
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState("");

  const handleVerDetalle = async (mesNum, mesNombre, categoria, categoriaLabel) => {
    setDetalleInfo({ mesNombre, categoria, categoriaLabel });
    setCargandoDetalle(true);
    setErrorDetalle("");
    try {
      const { data: resp } = await getDetalleMesServicio({ sucursal, year, mes: mesNum, categoria: categoria || undefined });
      setDetalle(resp);
    } catch (e) {
      setDetalle(null);
      setErrorDetalle(e?.response?.data?.error || "No se pudo cargar el detalle.");
    } finally {
      setCargandoDetalle(false);
    }
  };

  const handleCerrarDetalle = () => {
    setDetalleInfo(null);
    setDetalle(null);
    setErrorDetalle("");
  };

  const handleBuscar = async () => {
    handleCerrarDetalle();
    setLoading(true);
    setError("");
    try {
      const { data: resp } = await getReportePorAnio({ sucursal, year });
      setData(resp);
      setBuscado(true);
    } catch (e) {
      setData(null);
      setError(e?.response?.data?.error || "No se pudo generar el reporte.");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (searchParams.get("sucursal")) {
      handleBuscar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categorias = data?.categorias || [];

  // Excel generado en el navegador con lo que se ve en pantalla: encabezado en dos niveles, montos numéricos (no texto) y fila de totales.
  const handleExportar = () => {
    if (!data) return;
    const num = (v) => Number(v) || 0;
    const encabezado1 = ["Mes"];
    const encabezado2 = [""];
    categorias.forEach((c) => { encabezado1.push(c.label, ""); encabezado2.push("Bs.", "$us"); });
    encabezado1.push("Total por Mes", ""); encabezado2.push("Bs.", "$us");

    const filas = (data.meses || []).map((m) => {
      const fila = [m.nombre];
      categorias.forEach((c) => { const cat = m.categorias?.[c.key] || {}; fila.push(num(cat.bolivianos), num(cat.dolares)); });
      fila.push(num(m.totalPorMes?.bolivianos), num(m.totalPorMes?.dolares));
      return fila;
    });
    const totales = ["Totales"];
    categorias.forEach((c) => { const t = data.totalesPorCategoria?.[c.key] || {}; totales.push(num(t.bolivianos), num(t.dolares)); });
    totales.push(num(data.totales?.bolivianos), num(data.totales?.dolares));

    const aoa = [encabezado1, encabezado2, ...filas, totales];
    const hoja = XLSX.utils.aoa_to_sheet(aoa);
    hoja["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }];
    for (let i = 0; i <= categorias.length; i += 1) hoja["!merges"].push({ s: { r: 0, c: 1 + i * 2 }, e: { r: 0, c: 2 + i * 2 } });
    hoja["!cols"] = aoa[0].map((_, i) => ({ wch: i === 0 ? 14 : 13 }));
    for (let r = 2; r < aoa.length; r += 1) {
      for (let c = 1; c < aoa[r].length; c += 1) {
        const celda = hoja[XLSX.utils.encode_cell({ r, c })];
        if (celda) celda.z = "#,##0.00";
      }
    }
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, `${year}`);
    XLSX.writeFile(libro, `reporte_facturas_por_anio_${sucursal.replace(/\s+/g, "_")}_${year}.xlsx`);
  };
  const totalColumnas = 1 + categorias.length * 2 + 2; // Mes + (Bs+$us por categoria) + Total Bs + Total $us

  return (
    <DashboardLayout>
      <PageHeader
        title="Reporte por Año"
        subtitle="Totales por mes y categoría de servicio de facturas emitidas"
        backTo="/reporte-facturas"
      />
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
          <TextField
            select size="small" label="Sucursal"
            sx={{ ...compactFieldSx, width: 150 }}
            value={sucursal}
            onChange={(e) => setSucursal(e.target.value)}
          >
            {SUCURSALES.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: "0.85rem" }}>{s}</MenuItem>
            ))}
          </TextField>

          <TextField
            select size="small" label="Año"
            sx={{ ...compactFieldSx, width: 120 }}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {ANIOS.map((a) => (
              <MenuItem key={a} value={a} sx={{ fontSize: "0.85rem" }}>{a}</MenuItem>
            ))}
          </TextField>

          <Button
            size="small"
            variant="contained"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
            onClick={handleBuscar}
            disabled={loading}
            sx={{ minHeight: 32 }}
          >
            {loading ? "Buscando..." : "Buscar"}
          </Button>

          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportar}
            disabled={loading || !data}
            sx={{ minHeight: 32 }}
          >
            Exportar Excel
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!buscado ? (
          <Alert severity="info">Elige sucursal y año, y presiona Buscar.</Alert>
        ) : (
          <>
            <Paper variant="outlined">
              <TableContainer>
                <Table size="small" sx={{ "& td": { py: 0.35, px: 0.75, fontSize: "0.74rem", whiteSpace: "nowrap" } }}>
                  <TableHead>
                    <TableRow>
                      <StyledHeaderCell rowSpan={2} sx={{ verticalAlign: "bottom" }}>Mes</StyledHeaderCell>
                      {categorias.map((c) => (
                        <StyledHeaderCell key={c.key} colSpan={2} align="center">{c.label}</StyledHeaderCell>
                      ))}
                      <StyledHeaderCell colSpan={2} align="center">Total por Mes</StyledHeaderCell>
                    </TableRow>
                    <TableRow>
                      {categorias.map((c) => (
                        <React.Fragment key={c.key}>
                          <StyledHeaderCell align="right">Bs.</StyledHeaderCell>
                          <StyledHeaderCell align="right">$us</StyledHeaderCell>
                        </React.Fragment>
                      ))}
                      <StyledHeaderCell align="right">Bs.</StyledHeaderCell>
                      <StyledHeaderCell align="right">$us</StyledHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={totalColumnas} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : (
                      (data?.meses || []).map((m) => (
                        <TableRow key={m.mes} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{m.nombre}</TableCell>
                          {categorias.map((c) => {
                            const cat = m.categorias?.[c.key] || { bolivianos: 0, dolares: 0 };
                            return (
                              <React.Fragment key={c.key}>
                                <CeldaMonto
                                  valor={cat.bolivianos}
                                  onClick={() => handleVerDetalle(m.mes, m.nombre, c.key, c.label)}
                                />
                                <CeldaMonto
                                  valor={cat.dolares}
                                  onClick={() => handleVerDetalle(m.mes, m.nombre, c.key, c.label)}
                                />
                              </React.Fragment>
                            );
                          })}
                          <CeldaMonto
                            valor={m.totalPorMes?.bolivianos}
                            onClick={() => handleVerDetalle(m.mes, m.nombre, null, null)}
                            bold
                          />
                          <CeldaMonto
                            valor={m.totalPorMes?.dolares}
                            onClick={() => handleVerDetalle(m.mes, m.nombre, null, null)}
                            bold
                          />
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                  {!loading && data && (
                    <TableBody>
                      <TableRow sx={{ "& td": { fontWeight: 700, borderTop: "2px solid", borderColor: "divider" } }}>
                        <TableCell>Totales</TableCell>
                        {categorias.map((c) => {
                          const tot = data.totalesPorCategoria?.[c.key] || { bolivianos: 0, dolares: 0 };
                          return (
                            <React.Fragment key={c.key}>
                              <TableCell align="right">{formatMoneda(tot.bolivianos)}</TableCell>
                              <TableCell align="right">{formatMoneda(tot.dolares)}</TableCell>
                            </React.Fragment>
                          );
                        })}
                        <TableCell align="right">{formatMoneda(data.totales.bolivianos)}</TableCell>
                        <TableCell align="right">{formatMoneda(data.totales.dolares)}</TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Paper>

            <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, maxWidth: 320, ml: "auto" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Total bolivianos</Typography>
                <Typography variant="body2" fontWeight={700}>{formatMoneda(data?.totales?.bolivianos)}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                <Typography variant="body2">Total dólares</Typography>
                <Typography variant="body2" fontWeight={700}>{formatMoneda(data?.totales?.dolares)}</Typography>
              </Box>
            </Paper>

            {detalleInfo && (
              <Paper variant="outlined" sx={{ mt: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: "capitalize" }}>
                    Detalle — {detalleInfo.mesNombre} {year}
                    {detalleInfo.categoriaLabel ? ` · ${detalleInfo.categoriaLabel}` : " · Todas las categorías"}
                  </Typography>
                  <IconButton size="small" onClick={handleCerrarDetalle}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                {errorDetalle && <Alert severity="error" sx={{ m: 1.5 }}>{errorDetalle}</Alert>}

                {cargandoDetalle ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : detalle && (
                  <>
                    <TableContainer sx={{ maxHeight: "50vh" }}>
                      <Table size="small" stickyHeader sx={{ "& td": { py: 0.35, px: 0.75, fontSize: "0.74rem", whiteSpace: "nowrap" } }}>
                        <TableHead>
                          <TableRow>
                            <StyledHeaderCell>Cliente</StyledHeaderCell>
                            <StyledHeaderCell>Código Servicio</StyledHeaderCell>
                            <StyledHeaderCell>Tipo Factura</StyledHeaderCell>
                            <StyledHeaderCell>N° Factura</StyledHeaderCell>
                            <StyledHeaderCell>Fecha de Emisión</StyledHeaderCell>
                            <StyledHeaderCell>Mes de Servicio</StyledHeaderCell>
                            <StyledHeaderCell align="right">Monto Dólares</StyledHeaderCell>
                            <StyledHeaderCell align="right">Monto Bolivianos</StyledHeaderCell>
                            <StyledHeaderCell>Cobrada</StyledHeaderCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detalle.items.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={9} align="center" sx={{ py: 2, fontStyle: "italic", color: "text.secondary" }}>
                                Sin facturas.
                              </TableCell>
                            </TableRow>
                          ) : (
                            detalle.items.map((it, idx) => (
                              <TableRow key={idx} hover>
                                <TableCell>{it.nombre}</TableCell>
                                <TableCell>{it.cod_serv}</TableCell>
                                <TableCell>{it.tipo_factura}</TableCell>
                                <TableCell>{it.num_fac}</TableCell>
                                <TableCell>{formatFecha(it.fecha)}</TableCell>
                                <TableCell>{it.mes_servicio}</TableCell>
                                <TableCell align="right">{formatMoneda(it.dolares)}</TableCell>
                                <TableCell align="right">{formatMoneda(it.bolivianos)}</TableCell>
                                <TableCell>{it.pagada}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ display: "flex", gap: 3, justifyContent: "flex-end", p: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
                      <Typography variant="body2">Total Bolivianos: <b>{formatMoneda(detalle.totales.bolivianos)}</b></Typography>
                      <Typography variant="body2">Total Dólares: <b>{formatMoneda(detalle.totales.dolares)}</b></Typography>
                    </Box>
                  </>
                )}
              </Paper>
            )}
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ReporteFacturasPorAnioPage;
